import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
// REMOVED: import { motion } from 'framer-motion'; - for performance
import BrokerSelectionStep from './steps/BrokerSelectionStep';
import DataUploadStep from './steps/DataUploadStep';
import SuccessStep from './steps/SuccessStep';
import WizardProgress from './components/WizardProgress';
import { WizardState, BrokerConfig } from './types';

interface PortfolioConnectionWizardProps {
  onClose: () => void;
  preselectedBroker?: BrokerConfig | null;
}

const PortfolioConnectionWizard: React.FC<PortfolioConnectionWizardProps> = ({
  onClose,
  preselectedBroker,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [state, setState] = useState<WizardState>({
    currentStep: 0,
    selectedBroker: preselectedBroker,
    connectionMethod: 'csv', // Always CSV now
  });

  // Reset wizard when preselectedBroker changes
  useEffect(() => {
    setState({
      currentStep: 0,
      selectedBroker: preselectedBroker,
      connectionMethod: 'csv', // Always CSV now
    });
  }, [preselectedBroker]);

  const steps = [
    { id: 'broker', title: 'Select Broker' },
    { id: 'upload', title: 'Upload Data' },
    { id: 'success', title: 'Success' },
  ];

  const handleNext = useCallback((data?: any) => {
    setState((prev) => {
      const newState = { ...prev };

      switch (prev.currentStep) {
        case 0: // Broker selection
          newState.selectedBroker = data.broker;
          newState.currentStep = 1;
          break;
        case 1: // Data upload (CSV only)
          newState.csvFile = data.file;
          newState.currentStep = 2;
          break;
        case 2: // Success - close wizard
          onClose();
          // Navigate to portfolio overview
          navigate('/portfolio');
          break;
      }

      return newState;
    });
  }, [onClose, navigate]);

  const handleBack = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
    }));
  }, []);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const renderStep = () => {
    switch (state.currentStep) {
      case 0:
        return (
          <BrokerSelectionStep
            onNext={handleNext}
            onCancel={handleCancel}
          />
        );
      case 1:
        return (
          <DataUploadStep
            broker={state.selectedBroker!}
            onNext={handleNext}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        );
      case 2:
        return (
          <SuccessStep
            broker={state.selectedBroker!}
            onNext={handleNext}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{ minHeight: '100vh' }}
    >
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: 'background.default', // Use theme background
            borderBottom: `1px solid ${
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)'
            }`,
            py: 1.5, // Reduced from 2 to make header 4px shorter
            px: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              maxWidth: 1200,
              mx: 'auto',
            }}
          >
            {/* Back button - only show after first step */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {state.currentStep > 0 && state.currentStep < 2 && (
                <IconButton
                  onClick={handleBack}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {state.currentStep === 2 ? 'Upload Complete!' : 'Upload Portfolio Data'}
              </Typography>
            </Box>

            {/* Close button - only show before success step */}
            {state.currentStep < 2 && (
              <IconButton
                onClick={handleCancel}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Progress indicator */}
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 3 }}>
          <WizardProgress
            steps={steps}
            currentStep={state.currentStep}
          />
        </Box>

        {/* Step content with animation */}
        <Box
          sx={{
            maxWidth: 1200,
            mx: 'auto',
            px: 3,
            pb: 6,
          }}
        >
          <Fade
            in
            timeout={300}
            key={state.currentStep}
          >
            <Box>
              {renderStep()}
            </Box>
          </Fade>
        </Box>
      </Box>
    </Box>
  );
};

export default PortfolioConnectionWizard;